import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { SupportService } from 'apps/app-cic-ciam/src/services/support.service';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  concat,
  defer,
  firstValueFrom,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { Country } from '../../shared/country.model';
import { CommonValidators } from '../../functions/utils';
import { STATE_TOKEN, StateModel } from '../../app.states';

type FormControls = {
  account?: FormControl;
  verification?: FormControl;
};

type FormValues = {
  account?: string | null;
  verification?: string | null;
};

type FormValuesKeys = keyof FormValues;
type FormControlsKeys = keyof FormControls;

@Component({
  selector: 'ciciam-form-forgot-password',
  templateUrl: './form-forgot-password.component.html',
})
export class FormForgotPasswordComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() websiteName = 'Flowbite';
  @Input() set message(stream: Observable<string | null>) {
    this.subscriptions.add(
      stream.subscribe((x) => this.messageSubject.next(x))
    );
  }

  @Output() submit = new EventEmitter<{
    account: string;
    validateFactor: string;
    appId?: string | null;
  }>();
  @Output() backToLogin: EventEmitter<any> = new EventEmitter();
  @Output() forgotPassword: EventEmitter<any> = new EventEmitter();

  buttonGroupClasses = {
    selected: [
      'z-10',
      'ring-2',
      'ring-notus-primary-300',
      'bg-notus-primary-700',
      'text-white',
    ],
  };

  isLoading: boolean = false;
  isCountryListLoading: boolean = false;

  countryList: Country[] = [];

  @Select(STATE_TOKEN) appState$!: Observable<StateModel>;

  form1 = this.formBuilder.nonNullable.group<FormControls>(
    {
      account: new FormControl('', {
        validators: [Validators.required],
      }),
    }
  );
  form2 = this.formBuilder.nonNullable.group<FormControls>(
    {
      verification: new FormControl('', {
        validators: [Validators.required],
      }),
    }
  );

  get form1Values(): FormValues {
    return this.form1.getRawValue();
  }
  get form2Values(): FormValues {
    return this.form2.getRawValue();
  }

  accountControlSubscription?: Subscription;
  verificationControlSubscription?: Subscription;

  private readonly messageSubject = new BehaviorSubject<string | null>(null);
  public readonly message$ = this.messageSubject.asObservable();

  private readonly subscriptions = new Subscription();

  private subLoginApp?: Subscription;
  public isAllowRegister: boolean = true;

  isStep1: boolean = true;
  isStep1AccountEmail: boolean = false;

  appId: string | null = null;
  isUserTypeLoaded: boolean = false;
  userType: string = '';

  // Track submission attempts
  isStep1SubmitAttempted: boolean = false;
  isStep2SubmitAttempted: boolean = false;

  @ViewChild('verificationInput') verificationInput?: ElementRef<HTMLInputElement>;
  private wasStep1 = true;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly store: Store,
    private readonly translate: TranslateService,
    private supportService: SupportService,
    private toastr: ToastrService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.checkAppConfig();
  }

  async checkAppConfig() {
    // check if there is appid in url param
    const urlParams = new URLSearchParams(window.location.search);
    this.appId = urlParams.get('appid');
    if (this.appId) {
      let appconfig = await this.supportService.getLoginAppConfig(
        undefined,
        this.appId
      );
      this.userType = appconfig?.value?.extra?.userType ?? '';
    }
    this.isUserTypeLoaded = true;

    // // auto focus account input
    // setTimeout(() => {
    //   const accountControl = this.form1.get('account');
    //   if (accountControl) {
    //     const element = document.getElementById(accountControl?.value);
    //     if (element) {
    //       element.focus();
    //     }
    //   }
    // }, 10);
  }

  ngAfterViewChecked(): void {
    if (this.wasStep1 && !this.isStep1 && this.verificationInput) {
      this.verificationInput.nativeElement.focus();
    }
    this.wasStep1 = this.isStep1;
  }

  ngOnDestroy(): void {
    this.accountControlSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.subLoginApp?.unsubscribe();
  }

  async onSubmitForgotPasswordStep1() {
    this.messageSubject.next(null);
    this.isStep1SubmitAttempted = true;
    this.form1.markAllAsTouched();
    this.form1.updateValueAndValidity();
    if (this.form1.valid) {
      this.isLoading = true;
      let result = await this.supportService.lookupUser(
        this.form1Values.account!
      );
      this.isLoading = false;
      if (result !== false) {
        try {
          if (result.length > 0) {
            result = result[0];
          }
          if (this.form1Values.account!.indexOf('@') >= 0) {
            this.isStep1AccountEmail = true;
          }
          this.isStep1 = false;
        } catch (e: any) {
          // TODO handle error
          this.toastr.error(this.translateService.instant('User not found'));
        }
      } else {
        this.toastr.error(this.translateService.instant('User not found'));
      }
    }
  }

  onSubmitForgotPassword() {
    this.messageSubject.next(null);
    this.isStep2SubmitAttempted = true;

    if (this.form2.valid) {
      this.submit.emit({
        account: this.form1Values.account!,
        validateFactor: this.form2Values.verification!,
        appId: this.appId,
      });
      this.isLoading = true;
      // this.form.reset();
    } else {
      this.form2.updateValueAndValidity();
      this.form2.markAllAsTouched();
    }
  }

  onBackToLogin($event: any) {
    $event.preventDefault();
    this.backToLogin.emit();
  }

  async fetchCountryList(): Promise<void> {
    this.isCountryListLoading = true;
    var first: Country | null | undefined;

    try {
      this.countryList = await this.supportService.getCountries();
    } catch (error) {
      if (typeof error === 'string') {
        this.toastr.error(this.translate.instant(error));
      }
    } finally {
      this.isCountryListLoading = false;
    }
  }
}
